import React, { useEffect, useMemo, useRef } from "react";
import styles from "./MfDataTransmission.module.css";
import ModalInputField from "components/shared/modal/ModalInputField";
import { useMfImport } from "features/compositions/context/MfImportContext";
import { formatBytes } from "lib/formatBytes";
import useTheme from "config/useTheme";
import ActionProgress from "components/shared/status/ActionProgress";
import { useUploadMf } from "features/compositions/services/useMfImportService";
import ActionProgressSuccess from "components/shared/status/ActionProgressSuccess";
import { useCalcCreateCompProgress } from "config/useCalculationFramework";
import ActionProgressFailed from "components/shared/status/ActionProgressFailed";
import { validateFieldName } from "utils/validations";
import { useNavigate } from "react-router-dom";
import { useModalSystem } from "config/context/ModalSystemContext";
import ModalDropdown from "components/shared/modal/ModalDropdown";
import { useCompositionsContext } from "features/compositions/context/CompositionsContext";
import { useGetDatabaseScopesService } from "features/compositions/services/useGetDatabaseScopesService";
import GuiContext from "config/context/guiContext";
import { toComposition } from "features/compositions/utils/mfPayload";
import { POSTOJE_REDOVI } from "features/compositions/services/fileHandles";
import MfZateceno from "./MfZateceno";


const MfDataTransmission = () => {
    const navigate = useNavigate();
    const { dark } = useTheme();
    const { hideModal } = useModalSystem();
    const stepInterval = useCalcCreateCompProgress();
    const {
        file,
        instance,
        setInstance,
        selectedPermission,
        pkey,
        exist,
        selectedComposition,
        table,
        tables,
        collectTables,
        plugin,
        config,
        resetMfContext
    } = useMfImport();

    const { response, loading, uploadMf } = useUploadMf();

    // ZATEČENI SCOPE-OVI.
    //
    // „Instance" JE scope — pregrada u kojoj podaci žive. Da bi novi podaci
    // stali PORED postojećih, tu mora da stoji isti tekst kao prvi put. Dok je
    // ovo bilo samo polje za slobodan unos, omašeno ime je davalo uvoz koji
    // PROĐE, a podaci odu u drugi scope i izgledaju kao da ih nema. Tiha greška
    // koju korisnik ne može da vidi ni iz jedne poruke.
    //
    // Zato spisak zatečenih, uz mogućnost unosa novog — jedno ne isključuje
    // drugo: prvi uvoz u bazu nema šta da ponudi, a naknadni po pravilu ide u
    // postojeći scope.
    const { selected: { selectedAccount } } = React.useContext(GuiContext);
    const { databaseScopes } = useCompositionsContext();
    const { getDatabaseScopes } = useGetDatabaseScopesService();

    // Čarobnjak se otvara sa strane Settings, gde spisak scope-ova nije nužno
    // već dohvaćen — zato se traži ovde, a ne pretpostavlja.
    useEffect(() => { getDatabaseScopes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const knownScopes = useMemo(
        () => [...new Set((databaseScopes || []).map((s) => s.scope).filter(Boolean))],
        [databaseScopes],
    );

    // Predlog se upisuje SAMO jednom i samo u prazno polje — inače bi osvežen
    // spisak pregazio ono što je korisnik u međuvremenu otkucao.
    const prefilled = useRef(false);
    useEffect(() => {
        if (prefilled.current || instance) return;
        // Ime baze je konvencija GUI-ja: `DatabaseSelector` pri izboru baze
        // poziva `selectScope(databaseName)`. Ako takav scope već postoji, on je
        // i ovde tačan izbor; ako scope-ova nema, to je prvi uvoz.
        if (!knownScopes.length) {
            if (!selectedAccount) return;
            setInstance(selectedAccount);
        } else {
            setInstance(knownScopes.includes(selectedAccount) ? selectedAccount : knownScopes[0]);
        }
        prefilled.current = true;
    }, [knownScopes, selectedAccount, instance, setInstance]);

    // Izvor se više ne izvodi iz imena fajla — konekcioni izvori ga nemaju.
    // Plugin je izabran u koraku 1 i stoji u kontekstu.

    const asComposition = (t, name) => toComposition(t, name, selectedPermission);

    // Više celina u jednom prolazu: imena dolaze iz izvora, a `collectTables`
    // vraća spisak sa upisanim izmenama tekućeg jezička — bez toga bi se
    // poslednji pregledani izbor indeksa izgubio.
    const many = (tables?.length ?? 0) > 1;
    const compositions = many
        ? collectTables().map((t) => asComposition(t, t.name))
        : [asComposition(table, selectedComposition)];

    const data = {
        permission: "active",
        private_key: pkey,
        exist: exist,
        scope: instance,
        compositions,
        file: file,
        config: config,
    };

    const handleUpload = (append = false) => {
        if (!validateFieldName(instance)) { return; }
        uploadMf({ slug: plugin?.slug, source: plugin?.name, data: { ...data, append } });
    };

    // Kompozicija već ima redove: server nije upisao ništa i čeka potvrdu.
    // To nije neuspeh nego pitanje, pa se ne prikazuje kao greška.
    const trazPotvrdu = response?.code === POSTOJE_REDOVI;
    // Dugme kaže šta će se desiti: kompozicija sa identitetom reda zamenjuje,
    // bez njega dopisuje (vidi `existing[].effect` sa servera).
    const efekti = new Set((response?.data?.existing || []).map((z) => z.effect));
    const natpisPotvrde = efekti.size !== 1 ? 'Import into existing'
        : efekti.has('replace') ? 'Replace and import' : 'Append and import';

    const onProgressClose = () => {
        resetMfContext();
    }

    useEffect(() => {
        if (response?.status) {
            setTimeout(() => {
                hideModal();
                // Host vraća ime kompozicije kao string; stara ruta je vraćala
                // ceo niz kompozicija, pa je ovde stajalo `compositions[0].name`.
                // Rezerva je ono što je korisnik i uneo, da navigacija ne padne
                // na `undefined` ako se oblik odgovora opet promeni.
                // Kod više kompozicija se otvara PRVA — druge su na istom
                // nalogu, pa je korisnik odatle vidi u spisku.
                const created = response?.data?.composition
                    || response?.data?.compositions?.[0]?.composition
                    || selectedComposition;
                navigate(`/compositions?composition=${created}&scope=${instance}`);
            }, 2000);
        }
        // Namerno visi SAMO o odgovoru. Telo zatvara prozor i ODVODI sa ekrana;
        // da `instance` ili `selectedComposition` uđu u zavisnosti, njihova
        // promena dok je odgovor još uspešan zakazala bi drugu navigaciju.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [response]);

    return (
        <div className={`${styles.data_transmission} ${dark ? styles.dark : ""}`}>
            {/* Konekcioni izvori nemaju fajl — bez ovoga bi ovde stajalo prazno
                ime i „NaN" umesto veličine. */}
            <div className={styles.transmission_info}>
                {file ? (
                    <>
                        <p>file name: <span>{file.name}</span></p>
                        <p>file size: <span>{formatBytes(file.size)}</span></p>
                    </>
                ) : (
                    <>
                        <p>source: <span>{plugin?.name}</span></p>
                        {/* Šta se čita. Kod više celina se imenuju sve, jer je
                            to jedino mesto pre upisa gde se izbor još vidi. */}
                        <p>
                            reading: <span>
                                {config?.database}
                                {many
                                    ? `: ${compositions.map((c) => c.target || c.name).join(', ')}`
                                    : (config?.collection || config?.table || config?.pattern
                                        ? `.${config.collection || config.table || config.pattern}`
                                        : '')}
                            </span>
                        </p>
                    </>
                )}
            </div>
            <div className={styles.insert_instance}>
                <p>
                    Specify the target instance for this data.
                </p>
                {/* Spisak postoji samo kad u bazi već ima podataka. Polje za
                    unos ostaje uvek — nov scope je legitiman izbor. */}
                {knownScopes.length > 0 && (
                    <div className={styles.scope_row}>
                        <ModalDropdown
                            title={
                                knownScopes.includes(instance)
                                    ? instance
                                    : "Existing instance"
                            }
                            options={knownScopes}
                            setOption={(val) => setInstance(val)}
                        />
                        <span className={styles.scope_or}>or type a new one</span>
                    </div>
                )}
                <ModalInputField
                    placeholder="Instance"
                    value={instance}
                    setValue={(val) => setInstance(val)}
                    validator={(val) => validateFieldName(val)}
                />
                {/* Nov scope znači da se podaci NE vide uz postojeće. To nije
                    greška, ali mora da se kaže pre nego što se pošalje. */}
                {knownScopes.length > 0 && instance && !knownScopes.includes(instance) && (
                    <p className={styles.scope_warn}>
                        '{instance}' is a new instance — this data will not appear
                        next to what is already in {knownScopes.join(', ')}.
                    </p>
                )}
            </div>
            {loading && <ActionProgress stepInterval={stepInterval} />}
            {response?.status && <ActionProgressSuccess message={response?.message} onClose={onProgressClose} />}
            {response && !response?.status && !trazPotvrdu && <ActionProgressFailed message={response?.message} details={response?.error} onClose={onProgressClose} />}
            {trazPotvrdu && !loading ? (
                <div className={styles.potvrda}>
                    <MfZateceno stavke={response?.data?.existing} />
                    <div className={styles.potvrda_dugmad}>
                        {/* Korak 4 nema „Cancel import" — uvoz tu obično već teče.
                            Ovde još nije počeo, pa odustajanje mora da postoji. */}
                        <button className={styles.odustani_btn} onClick={hideModal}>
                            Cancel import
                        </button>
                        <button className={styles.submit_btn} onClick={() => handleUpload(true)}>
                            {natpisPotvrde}
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    disabled={!validateFieldName(instance) || loading}
                    className={styles.submit_btn}
                    onClick={() => handleUpload()}
                >
                    Start data transmission
                </button>
            )}
        </div>
    );
};

export default MfDataTransmission;